import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Briefcase, MapPin, Award, Calendar, Trash2, Edit, FileText, Volume2, Link as LinkIcon, Check, Video } from 'lucide-react';
import Button from '../../common/Button';
import { useScrollPosition } from '../../../hooks/useScrollPosition';
import type { JobSeekerProfile } from '../../../types/profiles';

interface JobSeekerCardProps {
  profile: JobSeekerProfile;
  userEmail?: string | null;
  onEdit: () => void;
  onDelete: () => void;
  canEdit?: boolean;
}

export default function JobSeekerCard({
  profile,
  userEmail,
  onEdit,
  onDelete,
  canEdit = false
}: JobSeekerCardProps) {
  const navigate = useNavigate();
  const { saveScrollPosition } = useScrollPosition({
    key: 'jobSeekersListScroll',
    storage: 'session'
  });
  const [copied, setCopied] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    saveScrollPosition();
    navigate(`/job-seekers/${profile.id}`);
  };

  return (
    <div onClick={handleClick} className="block cursor-pointer">
      <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
        {profile.photoUrl && (
          <div className="relative h-48 w-full">
            <img
              src={profile.photoUrl}
              alt="Profile"
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Job Seeker</h3>
              <p className="text-gray-600">{userEmail || 'Anonymous'}</p>
            </div>
            {canEdit && (
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  icon={Edit}
                  onClick={(e) => {
                    e.preventDefault();
                    onEdit();
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  icon={Trash2}
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.confirm('Are you sure you want to delete this profile?')) {
                      onDelete();
                    }
                  }}
                  className="text-red-600 hover:text-red-700 hover:border-red-600"
                >
                  Delete
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="flex items-center space-x-2">
              <Briefcase className="h-5 w-5 text-green-600" />
              <div>
                <h4 className="font-medium text-gray-900">Looking For</h4>
                <div className="flex flex-wrap gap-2 mt-1">
                  {profile.jobTypes.map((type, i) => (
                    <span key={i} className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm capitalize">
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <MapPin className="h-5 w-5 text-green-600" />
              <div>
                <h4 className="font-medium text-gray-900">Preferred Locations</h4>
                <div className="space-y-1 mt-1">
                  {profile.locations.map((location, i) => (
                    <div key={i} className="text-gray-600">{location || 'Any location'}</div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Award className="h-5 w-5 text-green-600" />
              <div>
                <h4 className="font-medium text-gray-900">Skills & Experience</h4>
                <div className="mt-1">
                  <div className="flex flex-wrap gap-2">
                    {profile.skills.map((skill, i) => (
                      <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {skill}
                      </span>
                    ))}
                  </div>
                  <div className="text-gray-600 mt-2">
                    Experience: {profile.experience || 'Not specified'}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Calendar className="h-5 w-5 text-green-600" />
              <div>
                <h4 className="font-medium text-gray-900">Availability & Pay</h4>
                <div className="space-y-1 mt-1">
                  <div className="text-gray-600">
                    Available: {profile.availability}
                  </div>
                  {profile.desiredPay && (
                    <div className="text-gray-600">
                      Desired Pay: ${profile.desiredPay}/hour
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="border-t border-gray-100 pt-4">
              <h4 className="font-medium text-gray-900 mb-2">Contact Information</h4>
              <div className="space-y-1 text-sm">
                {profile.email && (
                  <div className="text-gray-600">
                    Email: {profile.email}
                  </div>
                )}
                {profile.phone && (
                  <div className="text-gray-600">
                    Phone: {profile.phone}
                  </div>
                )}
              </div>
            </div>

            {/* Resume Link */}
            {profile.resumeUrl && (
              <div className="border-t border-gray-100 pt-4">
                <a
                  href={profile.resumeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 text-green-600 hover:text-green-700 font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FileText className="h-5 w-5" />
                  <span>View Resume</span>
                </a>
              </div>
            )}

            {/* Voice Introduction */}
            {profile.voiceIntroUrl && (
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <Volume2 className="h-5 w-5 text-green-600" />
                    <h4 className="font-medium text-gray-900">Voice Introduction</h4>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={copied ? Check : LinkIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(profile.voiceIntroUrl!);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="text-xs"
                  >
                    {copied ? 'Copied!' : 'Copy Link'}
                  </Button>
                </div>
                <audio
                  controls
                  className="w-full"
                  onClick={(e) => e.stopPropagation()}
                  style={{ height: '40px' }}
                >
                  <source src={profile.voiceIntroUrl} type="audio/webm" />
                  Your browser does not support the audio element.
                </audio>
              </div>
            )}

            {/* Video Introduction */}
            {profile.videoUrl && (
              <div className="border-t border-gray-100 pt-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Video className="h-5 w-5 text-green-600" />
                  <h4 className="font-medium text-gray-900">Video Introduction</h4>
                </div>
                <video
                  controls
                  className="w-full rounded-xl shadow-md"
                  onClick={(e) => e.stopPropagation()}
                  preload="metadata"
                >
                  <source src={profile.videoUrl} type="video/mp4" />
                  <source src={profile.videoUrl} type="video/webm" />
                  <source src={profile.videoUrl} type="video/quicktime" />
                  Your browser does not support the video tag.
                </video>
              </div>
            )}

            {/* Show badge for premium profiles */}
            <div className="mt-4">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Premium Profile
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}